import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Stack, Heading, Badge, Text, Box, IconDocument, IconBookmark, IconPersonVerified, Divider, IconLink } from 'braid-design-system';
import { getSystemHealth, SystemHealthResponse } from '../services/systemHealthService';
import { useWebSocket } from '../contexts/WebSocketProvider';

const SystemHealthCard: React.FC = () => {
    const [health, setHealth] = React.useState<SystemHealthResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
    const { status: wsStatus } = useWebSocket();

    React.useEffect(() => {
        const fetchHealth = async () => {
            try {
                setLoading(true);
                const healthData = await getSystemHealth();
                setHealth(healthData);
                setLastUpdated(new Date());
            } catch (error) {
                // Keep showing last known state if fetch fails
                // eslint-disable-next-line no-console
                console.error('Failed to fetch system health:', error);
            } finally {
                setLoading(false);
            }
        };

        // Fetch health immediately
        fetchHealth();

        // Refresh every 30 seconds
        const interval = setInterval(fetchHealth, 30000);

        return () => clearInterval(interval);
    }, []);

    const { t } = useTranslation();

    const getStatusBadgeTone = (isHealthy: boolean) => {
        return isHealthy ? 'positive' : 'critical';
    };

    const getStatusText = (isHealthy: boolean) => {
        return isHealthy ? t('status.healthy', 'Healthy') : t('status.unhealthy', 'Unhealthy');
    };

    const getOverallStatusTone = (status?: string, wsStatus?: string) => {
        // If WebSocket is disconnected, overall status is unhealthy
        if (wsStatus === 'disconnected') return 'critical';
        // If WebSocket is connecting, overall status is warning
        if (wsStatus === 'connecting') return 'caution';

        if (status === 'healthy') return 'positive';
        if (status === 'degraded') return 'caution';
        return 'critical';
    };

    const getOverallStatusText = (status: string | undefined, wsStatus?: string): string => {
        // If WebSocket is disconnected, overall status is Unhealthy
        if (wsStatus === 'disconnected') return t('status.unhealthy', 'Unhealthy');
        // If WebSocket is connecting, overall status is Warning
        if (wsStatus === 'connecting') return t('status.warning', 'Warning...');

        if (status) {
            // capitalize
            return status.charAt(0).toUpperCase() + status.slice(1);
        }
        return t('status.unknown', 'Unknown');
    };

    return (
        <Card>
            <Stack space="medium">
                <Heading level="3" icon={<IconPersonVerified />}>{t('component.system_health.title', 'System Health')}</Heading>

                {loading && !health && (
                    <Text tone="secondary">{t('component.system_health.loading', 'Loading system status...')}</Text>
                )}

                {health && (
                    <Stack space="small">
                        {/* Overall Status */}
                        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                            <Text weight="strong">{t('component.system_health.overall_status', 'Overall Status')}</Text>
                            <Badge tone={getOverallStatusTone(health.status, wsStatus)}>
                                {getOverallStatusText(health.status, wsStatus)}
                            </Badge>
                        </Box>
                        <Divider />
                        {/* Service Status */}
                        <Stack space="xsmall">
                            {/* Cache Service */}
                            <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                <Text size='small' icon={<IconBookmark />}>{t('component.system_health.queue_service', 'Queue Service')}</Text>
                                <Badge tone={getStatusBadgeTone(health.cache_service)}>
                                    {getStatusText(health.cache_service)}
                                </Badge>
                            </Box>

                            {/* Report Service */}
                            <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                <Text size='small' icon={<IconDocument />}>{t('component.system_health.report_service', 'Report Service')}</Text>
                                <Badge tone={getStatusBadgeTone(health.report_service)}>
                                    {getStatusText(health.report_service)}
                                </Badge>
                            </Box>

                            {/* Report Service */}


                            {/* WebSocket Status */}
                            <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                <Text size='small' icon={<IconLink />}>{t('component.system_health.websocket', 'Web-Socket')}</Text>
                                {wsStatus === 'connecting' && (
                                    <Badge tone="caution">
                                        {t('topbar.connecting', 'connecting...')}
                                    </Badge>
                                )}
                                {wsStatus === 'connected' && (
                                    <Badge tone="positive">
                                        {t('status.healthy', 'Healthy')}
                                    </Badge>
                                )}
                                {wsStatus === 'disconnected' && (
                                    <Badge tone="critical">
                                        {t('status.unhealthy', 'Unhealthy')}
                                    </Badge>
                                )}
                            </Box>
                        </Stack>
                        <Divider />
                        {/* Last Updated */}
                        {lastUpdated && (
                            <Text tone="secondary" size="small">
                                {t('component.system_health.last_check', 'Last Health Check')}: {lastUpdated.toLocaleTimeString()}
                            </Text>
                        )}
                    </Stack>
                )}
            </Stack>
        </Card>
    );
};

export default SystemHealthCard;
